// Map marker palette.
//
// Every pin is a small enamel badge: a rich category tone, a crisp white rim and
// a single glint of light in the corner, lifted off the tiles by a real shadow.
// Tones stay deep on purpose, so a color emoji sitting inside always reads at a
// glance over light and dark basemaps alike.

const PIN_TONES = {
  Restaurant: ["#FDBA74", "#C2410C"], // warm amber
  Food: ["#FDBA74", "#C2410C"],
  Bakery: ["#FCD34D", "#B45309"],
  "Café": ["#D6D3D1", "#44403C"],
  Bar: ["#C4B5FD", "#5B21B6"], // deep violet
  Nightlife: ["#F9A8D4", "#9D174D"],
  Music: ["#F9A8D4", "#9D174D"],
  "LGBTQ+": ["#FDA4AF", "#9F1239"],
  Beach: ["#67E8F9", "#075985"], // teal
  Culture: ["#5EEAD4", "#0F4F4A"], // teal
  Shop: ["#BEF264", "#3F6212"],
  Experience: ["#93C5FD", "#1E40AF"],
  Fitness: ["#A5B4FC", "#3730A3"],
  Wellness: ["#5EEAD4", "#115E59"],
  Salon: ["#F9A8D4", "#9D174D"],
  Nails: ["#FDA4AF", "#9F1239"],
  Tattoo: ["#C4B5FD", "#5B21B6"],
  Barbershop: ["#93C5FD", "#1E3A8A"],
  Transport: ["#CBD5E1", "#334155"],
  Airport: ["#E2E8F0", "#1E293B"],
  "Local service": ["#94A3B8", "#1E293B"],
  Place: ["#CBD5E1", "#1E293B"],
};

/** Ink used for the marker bodies that aren't tied to a category. */
export const PIN_SLATE = "#1E293B";
export const PIN_BORDER = "1.5px solid #FFFFFF";
export const PIN_LIFT = "drop-shadow(0px 2px 6px rgba(0, 0, 0, 0.32))";

const clamp = (value) => Math.max(0, Math.min(255, Math.round(value)));

const parseHex = (hex) => {
  const clean = String(hex).replace("#", "");
  const full = clean.length === 3 ? clean.split("").map((c) => c + c).join("") : clean;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
};

const toHex = ([r, g, b]) => `#${[r, g, b].map((v) => clamp(v).toString(16).padStart(2, "0")).join("")}`;

/** Lighten (positive) or darken (negative) a hex color by a 0–1 amount. */
export const shade = (hex, amount) =>
  toHex(parseHex(hex).map((v) => (amount >= 0 ? v + (255 - v) * amount : v * (1 + amount))));

export const rgba = (hex, alpha) => `rgba(${parseHex(hex).join(", ")}, ${alpha})`;

export function pinTone(label) {
  return PIN_TONES[label] || PIN_TONES.Place;
}

/**
 * Surface for a marker disc: category gradient, white rim, corner glint and —
 * when selected — a glow in the marker's own color so a tap reads instantly.
 */
export function pinDisc({ label, active = false, highlighted = false }) {
  const [light, deep] = pinTone(label);
  // A consistent graphite enamel surface reads cleanly against both basemap
  // themes. Category-colored hairline rims preserve the quick visual language
  // without oversized neon discs competing with the city itself.
  const rings = [];
  if (active) rings.push("0 0 0 3px rgba(255, 65, 80, 0.50)", "0 0 0 7px rgba(255, 65, 80, 0.14)");
  else if (highlighted) rings.push(`0 0 0 2px ${rgba(light, 0.44)}`);
  return {
    background: `linear-gradient(155deg, ${shade(deep, -0.25)} 0%, #252629 48%, #111315 100%)`,
    border: `1.5px solid ${rgba(light, active ? 1 : 0.88)}`,
    boxShadow: [
      ...rings,
      "inset 0 1px 0 rgba(255, 255, 255, 0.22)",
      "inset 0 -1px 3px rgba(0, 0, 0, 0.25)",
    ].join(", "),
  };
}

/** The glyph itself: a hair of shadow so the emoji sits in the badge, not on it. */
export const PIN_GLYPH = {
  lineHeight: 1,
  fontFamily: "'Apple Color Emoji','Segoe UI Emoji','Noto Color Emoji','Twemoji Mozilla',sans-serif",
  filter: "drop-shadow(0 1px 1.5px rgba(0, 0, 0, 0.45))",
  pointerEvents: "none",
};